import { useState, useEffect } from "react";

// E-ink display utilities for Birch

export type RefreshFrequency = "low" | "normal" | "high";
export type ZoomLevel = "50" | "75" | "100" | "150" | "200";
export type ColorTheme = "default" | "eye-friendly" | "inverted";

export const ZOOM_CHANGE_EVENT = "birch-zoom-change";

const REFRESH_FREQUENCY_KEY = "deckle-refresh-frequency";
const ZOOM_LEVEL_KEY = "deckle-zoom-level";
const COLOR_THEME_KEY = "deckle-color-theme";

/**
 * Trigger a full screen refresh on e-ink displays.
 * This creates a brief black overlay to force a complete screen redraw,
 * which clears ghosting artifacts common on e-paper.
 */
export function triggerFullRefresh(): void {
  const overlay = document.createElement("div");
  overlay.style.cssText = `
    position: fixed;
    inset: 0;
    background: #000;
    z-index: 99999;
    pointer-events: none;
  `;
  document.body.appendChild(overlay);
  
  // Remove after a brief moment to force refresh
  setTimeout(() => {
    overlay.remove();
  }, 50);
}

/**
 * Get the current refresh frequency setting.
 */
export function getRefreshFrequency(): RefreshFrequency {
  const stored = localStorage.getItem(REFRESH_FREQUENCY_KEY);
  if (stored === "low" || stored === "normal" || stored === "high") {
    return stored;
  }
  return "normal";
}

/**
 * Set the refresh frequency preference.
 */
export function setRefreshFrequency(frequency: RefreshFrequency): void {
  localStorage.setItem(REFRESH_FREQUENCY_KEY, frequency);
}

/**
 * Get the number of page turns before triggering an automatic refresh.
 * Based on the refresh frequency setting.
 */
export function getRefreshInterval(): number {
  const frequency = getRefreshFrequency();
  switch (frequency) {
    case "low":
      return 10; // Refresh every 10 page turns
    case "normal":
      return 5; // Refresh every 5 page turns
    case "high":
      return 2; // Refresh every 2 page turns
    default:
      return 5;
  }
}

/**
 * Get the current zoom level setting.
 */
export function getZoomLevel(): ZoomLevel {
  const stored = localStorage.getItem(ZOOM_LEVEL_KEY);
  if (stored === "50" || stored === "75" || stored === "100" || stored === "150" || stored === "200") {
    return stored;
  }
  if (stored === "125" || stored === "175") {
    return stored as ZoomLevel;
  }
  return "100";
}

/**
 * Get current numerical zoom factor (e.g. 1.0, 1.5, 2.0).
 */
export function getZoomFactor(level?: ZoomLevel): number {
  const zoomLevel = level ?? getZoomLevel();
  const percentage = parseInt(zoomLevel, 10);
  return Number.isFinite(percentage) && percentage > 0 ? percentage / 100 : 1;
}

/**
 * Set the zoom level preference and notify subscribers.
 */
export function setZoomLevel(level: ZoomLevel): void {
  localStorage.setItem(ZOOM_LEVEL_KEY, level);
  applyZoomLevel(level);

  if (typeof window !== "undefined") {
    const factor = getZoomFactor(level);
    window.dispatchEvent(
      new CustomEvent(ZOOM_CHANGE_EVENT, {
        detail: { zoomLevel: level, zoomFactor: factor },
      })
    );
  }
}

/**
 * Apply zoom level to the document.
 * Uses font-size on html element to scale all rem/em units.
 * Also synchronizes --zoom-factor CSS variable and data-zoom-level attribute.
 */
export function applyZoomLevel(level?: ZoomLevel): void {
  const zoomLevel = level ?? getZoomLevel();
  const percentage = parseInt(zoomLevel, 10);
  const factor = Number.isFinite(percentage) && percentage > 0 ? percentage / 100 : 1;

  if (typeof document !== "undefined") {
    document.documentElement.style.fontSize = `${percentage}%`;
    document.documentElement.style.setProperty("--zoom-factor", `${factor}`);
    document.documentElement.setAttribute("data-zoom-level", zoomLevel);
  }
}

/**
 * React hook that reactively tracks the current zoom factor.
 */
export function useZoomFactor(): number {
  const [factor, setFactor] = useState<number>(() => getZoomFactor());

  useEffect(() => {
    const handleZoomChange = (e?: Event) => {
      if (e instanceof CustomEvent && e.detail?.zoomFactor) {
        setFactor(e.detail.zoomFactor);
      } else {
        setFactor(getZoomFactor());
      }
    };

    window.addEventListener(ZOOM_CHANGE_EVENT, handleZoomChange);
    window.addEventListener("storage", handleZoomChange);

    return () => {
      window.removeEventListener(ZOOM_CHANGE_EVENT, handleZoomChange);
      window.removeEventListener("storage", handleZoomChange);
    };
  }, []);

  return factor;
}

/**
 * Initialize zoom level on app load.
 */
export function initializeZoomLevel(): void {
  applyZoomLevel();
}

/**
 * Get the current color theme setting.
 */
export function getColorTheme(): ColorTheme {
  const stored = localStorage.getItem(COLOR_THEME_KEY);
  if (stored === "default" || stored === "eye-friendly" || stored === "inverted") {
    return stored;
  }
  return "default";
}

/**
 * Set the color theme preference.
 */
export function setColorTheme(theme: ColorTheme): void {
  localStorage.setItem(COLOR_THEME_KEY, theme);
  applyColorTheme(theme);
}

/**
 * Apply color theme to the document.
 * Adds/removes theme class on html element.
 */
export function applyColorTheme(theme?: ColorTheme): void {
  const colorTheme = theme ?? getColorTheme();
  const html = document.documentElement;
  
  // Remove existing theme classes
  html.classList.remove("theme-default", "theme-eye-friendly", "theme-inverted");
  
  // Add the current theme class
  html.classList.add(`theme-${colorTheme}`);
}

/**
 * Initialize color theme on app load.
 */
export function initializeColorTheme(): void {
  applyColorTheme();
}
